import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Skeleton } from "../components/ui/skeleton";
import { Settings, Shield, CheckCircle2, AlertCircle, RefreshCw, Key } from "lucide-react";
import { toast } from "sonner";

const PROVIDERS = [
  { id: "heygen", name: "HeyGen", desc: "Avatar video generation", color: "#6366f1" },
  { id: "atlas_cloud", name: "Atlas Cloud", desc: "Full-modal AI inference platform", color: "#0ea5e9" },
  { id: "elevenlabs", name: "ElevenLabs", desc: "Voice & narration synthesis", color: "#1a1a1a" },
  { id: "assemblyai", name: "AssemblyAI", desc: "Audio transcription & word-level timestamps", color: "#ef4444" },
];

export default function Integrations() {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConnect, setShowConnect] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [stagingKey, setStagingKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);

  const fetchIntegrations = useCallback(async () => {
    try {
      const res = await api.get("/integrations");
      setIntegrations(res.data);
    } catch { toast.error("Failed to load integrations"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchIntegrations(); }, [fetchIntegrations]);

  const getIntegration = (provider) => integrations.find(i => i.provider === provider);
  const currentProvider = PROVIDERS.find(p => p.id === showConnect);

  const handleSave = async () => {
    const isDual = currentProvider?.dualKey;
    if (isDual) {
      if (!stagingKey.trim() && !apiKey.trim()) return;
      setSaving(true);
      try {
        await api.post("/integrations", {
          provider: showConnect,
          api_key: apiKey,
          staging_api_key: stagingKey,
          env_scope: "dual"
        });
        toast.success(`${currentProvider.name} connected`);
        setShowConnect(null); setApiKey(""); setStagingKey("");
        fetchIntegrations();
      } catch { toast.error("Failed to save integration"); }
    } else {
      if (!apiKey.trim()) return;
      setSaving(true);
      try {
        await api.post("/integrations", { provider: showConnect, api_key: apiKey, env_scope: "dev" });
        toast.success(`${currentProvider?.name} connected`);
        setShowConnect(null); setApiKey("");
        fetchIntegrations();
      } catch { toast.error("Failed to save integration"); }
    }
    setSaving(false);
  };

  const handleTest = async (integId, providerName) => {
    setTesting(integId);
    try {
      const res = await api.post(`/integrations/${integId}/test`);
      if (res.data.status === 'Connected') {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
      fetchIntegrations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Connection test failed");
    }
    setTesting(null);
  };

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="integrations-page">
      <div className="flex items-center gap-3 mb-2">
        <Settings size={24} className="text-[#4D11F6]" strokeWidth={1.5} />
        <h1 className="text-3xl font-bold text-[#020B23]" style={{ fontFamily: 'Sora, sans-serif' }}>Integrations</h1>
      </div>
      <p className="text-[#676970] mb-8">Connect your provider API keys to enable video generation</p>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[1,2,3,4].map(i => (
            <div key={i} className="bg-white rounded-xl border border-[#E2E4E7] p-6">
              <Skeleton className="h-5 w-1/3 mb-3" />
              <Skeleton className="h-3 w-2/3 mb-4" />
              <Skeleton className="h-9 w-24" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {PROVIDERS.map(provider => {
            const integ = getIntegration(provider.id);
            const connected = integ?.status === "Connected";

            return (
              <div
                key={provider.id}
                data-testid={`provider-card-${provider.id}`}
                className="bg-white rounded-xl border border-[#E2E4E7] p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: provider.color }}>
                    <Key size={18} className="text-white" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-[#020B23]" style={{ fontFamily: 'Sora, sans-serif' }}>{provider.name}</h3>
                      {connected ? (
                        <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-100 text-xs shrink-0 whitespace-nowrap">
                          <CheckCircle2 size={12} className="mr-1" /> Connected
                        </Badge>
                      ) : (
                        <Badge className="bg-[#F3F3F9] text-[#676970] border border-[#E2E4E7] text-xs shrink-0 whitespace-nowrap">
                          Not Connected
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-[#94969C] mt-0.5">{provider.desc}</p>
                  </div>
                </div>

                {connected && (
                  <div className="mb-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <Shield size={14} className="text-[#94969C] shrink-0" />
                      <span className="text-sm text-[#676970] font-mono truncate">{integ.api_key_masked}</span>
                    </div>
                    {integ.staging_key_masked && (
                      <div className="flex items-center gap-2">
                        <Shield size={14} className="text-[#94969C] shrink-0" />
                        <span className="text-xs text-[#94969C]">Staging:</span>
                        <span className="text-sm text-[#676970] font-mono truncate">{integ.staging_key_masked}</span>
                      </div>
                    )}
                    {integ.last_verified && (
                      <p className="text-xs text-[#94969C]">
                        Verified: {new Date(integ.last_verified).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setShowConnect(provider.id); setApiKey(""); setStagingKey(""); }}
                    data-testid={`connect-${provider.id}-btn`}
                    className="border-[#E2E4E7] text-[#1A2238] hover:bg-[#F3F3F9] text-sm"
                  >
                    {connected ? "Update Key" : "Connect"}
                  </Button>
                  {connected && integ?.id && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTest(integ.id, provider.name)}
                      disabled={testing === integ.id}
                      data-testid={`test-${provider.id}-btn`}
                      className="border-[#E2E4E7] text-[#1A2238] hover:bg-[#F3F3F9] text-sm whitespace-nowrap"
                    >
                      <RefreshCw size={13} className={`mr-1.5 shrink-0 ${testing === integ.id ? "animate-spin" : ""}`} />
                      Test
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Connect Dialog */}
      <Dialog open={!!showConnect} onOpenChange={() => setShowConnect(null)}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="connect-key-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-[#020B23]" style={{ fontFamily: 'Sora, sans-serif' }}>
              Connect {currentProvider?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {currentProvider?.dualKey ? (
              <>
                <div className="space-y-2">
                  <Label className="text-[#1A2238] font-medium">Staging API Key</Label>
                  <Input
                    type="password"
                    placeholder="Enter your staging API key"
                    value={stagingKey}
                    onChange={(e) => setStagingKey(e.target.value)}
                    data-testid="integration-staging-key-input"
                    className="h-10 bg-white border-[#E2E4E7] font-mono text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1A2238] font-medium">Production API Key</Label>
                  <Input
                    type="password"
                    placeholder="Enter your production API key"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    data-testid="integration-api-key-input"
                    className="h-10 bg-white border-[#E2E4E7] font-mono text-sm"
                  />
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <Label className="text-[#1A2238] font-medium">API Key</Label>
                <Input
                  type="password"
                  placeholder="Enter your API key"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  data-testid="integration-api-key-input"
                  className="h-10 bg-white border-[#E2E4E7] font-mono text-sm"
                />
              </div>
            )}
            <div className="flex items-start gap-2 p-3 rounded-lg bg-[#FFF5EC] border border-[#FFD5B1]">
              <AlertCircle size={16} className="text-[#D07B30] mt-0.5 shrink-0" />
              <p className="text-xs text-[#744218]">
                API keys are encrypted at rest and never displayed after saving.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConnect(null)} className="border-[#E2E4E7] text-[#676970]">Cancel</Button>
            <Button onClick={handleSave} disabled={saving || (!apiKey.trim() && !stagingKey.trim())} data-testid="save-integration-btn"
              className="bg-[#4D11F6] hover:bg-[#3A0BC0] text-white">
              {saving ? "Saving..." : "Save & Connect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
